import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { AsTypePipe } from '@lassi/webapp-shared-pipes';
import {
  IStepOption,
  TourService,
  TourStepTemplateComponent,
} from 'ngx-ui-tour-md-menu';
import { OnboardingService } from './onboarding.service';

@Component({
  selector: 'lassi-onboarding',
  imports: [
    TourStepTemplateComponent,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    AsTypePipe,
    MatProgressBarModule,
  ],
  template: `
    <tour-step-template>
      <ng-template let-currentStep="step">
        @let step = currentStep | asType: stepOption;

        <mat-card
          (click)="$event.stopPropagation()"
          [style.width]="step.stepDimensions?.width"
          [style.min-width]="step.stepDimensions?.minWidth"
          [style.max-width]="step.stepDimensions?.maxWidth"
        >
          <mat-card-header>
            <div class="header-group">
              <mat-card-title>
                {{ step.title }}
              </mat-card-title>

              <button
                mat-icon-button
                (click)="
                  tourService.hasNext(tourService.currentStep)
                    ? onboarding.skipTour()
                    : onboarding.end()
                "
                class="close"
              >
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </mat-card-header>

          @if (tourService.steps.indexOf(step)) {
            <mat-progress-bar
              [value]="
                (tourService.steps.indexOf(step) * 100) /
                (tourService.steps.length - 1)
              "
            />
          }

          <mat-card-content class="mat-body" [innerHTML]="step.content" />

          @if (tourService.steps.indexOf(step)) {
            <mat-card-actions [class.no-progress]="!step.showProgress">
              <button
                mat-flat-button
                [disabled]="!tourService.hasPrev(step)"
                (click)="tourService.prev()"
              >
                <mat-icon>chevron_left</mat-icon>
                {{ step.prevBtnTitle }}
              </button>
              @if (tourService.hasNext(step) && !step.nextOnAnchorClick) {
                <button (click)="tourService.next()" mat-flat-button>
                  {{ step.nextBtnTitle }}
                  <mat-icon iconPositionEnd>chevron_right</mat-icon>
                </button>
              }
              @if (!tourService.hasNext(step)) {
                <button mat-flat-button (click)="onboarding.end()">
                  {{ step.endBtnTitle }}
                </button>
              }
              <div [style.flex-grow]="1"></div>
              @if (tourService.hasNext(step)) {
                <button mat-button (click)="onboarding.skipTour()">Skip</button>
              }
            </mat-card-actions>
          } @else {
            <mat-card-actions class="start-page">
              <button mat-flat-button (click)="tourService.next()">
                Start the tour
              </button>

              <button mat-button (click)="onboarding.skipTour()">
                Skip to map
              </button>
            </mat-card-actions>
          }
        </mat-card>
      </ng-template>
    </tour-step-template>
  `,
  styles: `
    @use '@angular/material' as mat;

    @include mat.icon-button-density(-3);

    mat-card {
      box-shadow: none;

      @include mat.card-overrides(
        (
          elevated-container-shape: var(--mat-menu-container-shape),
        )
      );
    }

    mat-card-content {
      margin: 8px 0;
    }

    .progress {
      font-size: 12px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.38);
      white-space: nowrap;
    }

    mat-card-actions {
      align-items: center;
      gap: 8px;

      &.start-page {
        display: flex;
        justify-content: space-between;
      }

      > * {
        max-width: fit-content;

        &:last-child {
          justify-self: flex-end;
        }
      }

      button.prev {
        padding-left: 4px;
      }

      button.next {
        padding-right: 4px;
      }
    }

    mat-card-header {
      .header-group {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: -8px;
        width: 100%;

        mat-card-title {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        button {
          margin-right: -8px;
        }
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingComponent {
  readonly tourService = inject(TourService);
  readonly onboarding = inject(OnboardingService);
  stepOption!: IStepOption;
}
